import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ContentMarkdown from "../../ContentMarkdown";
import { PolicyTextStyle } from "../../PolicyTextStyle";
import { Select } from "../../ui/Select";
import { useApiQuery } from "../../../services/api";

type UserMeResponse = Extract<
  ReturnType<typeof useApiQuery<"/webapp/private/users/me">>,
  { state: "success" }
>["body"];

const EmptyPartnerTab = () => {
  const { t } = useTranslation();
  return (
    <div className="py-4 flex flex-col gap-2">
      <div className="text-2xl font-bold text-primary-black text-center">
        {t("No partner terms available", "No partner terms available")}
      </div>
      <div className="text-base font-normal text-secondary-bluegrey-6 text-center">
        {t(
          "You don't have any rooms with Terms & Conditions.",
          "You don't have any rooms with Terms & Conditions."
        )}
      </div>
    </div>
  );
};

/**
 * Per-room partner Terms & Conditions (room selector + markdown). Shared by
 * the /terms page (Partners tab) and the settings sheet's LegalView.
 */
export const PartnerTermsContent = ({
  isLoading,
  roomUserSettings,
}: {
  isLoading: boolean;
  roomUserSettings: UserMeResponse["roomUserSettings"];
}) => {
  const { t } = useTranslation();
  const [selectedRoomId, setSelectedRoomId] = useState<string>("");

  // Calculate rooms with terms
  const roomsWithTerms = roomUserSettings.filter(
    (setting) => setting.room.termsConditions?.enabled
  );

  // Track room IDs to detect when the list changes
  const roomIds = roomsWithTerms.map((r) => r.roomId).join(",");

  // Set default selected room and handle removed rooms
  useEffect(() => {
    if (roomsWithTerms.length === 0) return;

    // Set default selected room if not set
    if (!selectedRoomId && roomsWithTerms.length > 0) {
      setSelectedRoomId(roomsWithTerms[0]?.roomId ?? "");
      return;
    }

    // Reset selection if the currently selected room is no longer available
    if (
      selectedRoomId &&
      !roomsWithTerms.some((r) => r.roomId === selectedRoomId)
    ) {
      setSelectedRoomId(roomsWithTerms[0]?.roomId ?? "");
    }
  }, [roomIds, selectedRoomId]);

  if (roomsWithTerms.length === 0) return <EmptyPartnerTab />;

  const selectedRoomSetting = roomsWithTerms.find(
    (s) => s.roomId === selectedRoomId
  );

  const termsContent = selectedRoomSetting?.room.termsConditions?.content ?? "";

  const handleRoomChange = (option: { value: string }): void => {
    setSelectedRoomId(option.value);
  };

  const roomOptions = roomsWithTerms.map((setting) => {
    const orgName = setting.room.organisationModel?.name;
    const label =
      orgName !== undefined && orgName !== null && orgName !== ""
        ? `${setting.room.name} - ${orgName}`
        : setting.room.name;
    return { label, value: setting.roomId };
  });

  return (
    <div>
      {/* Room Selector Dropdown */}
      <div className="mb-6 max-w-md">
        <Select
          appearance="field"
          footerAction={null}
          searchOnlyOptions={null}
          value={selectedRoomId}
          onChange={handleRoomChange}
          disabled={isLoading}
          options={roomOptions}
          placeholder={t("Select Room", "Select Room")}
          className="bg-white"
        />
      </div>

      {/* Terms & Conditions Content */}
      {termsContent ? (
        <PolicyTextStyle>
          <ContentMarkdown math={false} linkVariant="anchor">
            {termsContent}
          </ContentMarkdown>
        </PolicyTextStyle>
      ) : (
        <div className="py-4">
          <div className="text-base text-secondary-bluegrey-6 text-center">
            {t(
              "Terms & Conditions not available in the current language.",
              "Terms & Conditions not available in the current language."
            )}
          </div>
        </div>
      )}
    </div>
  );
};
